import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import API_BASE_URL from '../apiConfig';
import './TokenToUsdtPage.css';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const TokenToUsdtPageComponent = ({ isTelegramMiniApp, setWallets }) => {
  const { user, updateUserDetails } = useContext(AuthContext);
  const { t } = useTranslation('depositWithdrawAll');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const tokenBalance = user?.wallets?.token || 0;
  const maxSteps = Math.floor(tokenBalance / 100);
  const exchangeOptions = Array.from({ length: maxSteps }, (_, i) => (i + 1) * 100);
  const [selectedAmount, setSelectedAmount] = useState(maxSteps > 0 ? 100 : 0);

  useEffect(() => {
    if (maxSteps > 0 && selectedAmount === 0) {
      setSelectedAmount(100);
    } else if (maxSteps === 0) {
      setSelectedAmount(0);
    }
  }, [maxSteps, selectedAmount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAmount < 1000) {
      setMessage(t('tokenToUsdt.minAmountError'));
      const button = document.querySelector(".tokenToUsdt-submit-button");
      button.classList.add("tokenToUsdt-error-button");
      setTimeout(() => {
        button.classList.remove("tokenToUsdt-error-button");
        setMessage('');
      }, 3000);
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const response = await apiRequest(`${API_BASE_URL}/api/exchange-tokens`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
  amount: selectedAmount,
  convertTo: isTelegramMiniApp ? "stars" : "usdt"
})
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.message || t('tokenToUsdt.exchangeRequestFailed'));
        setIsProcessing(false);
        return;
      }

      setIsProcessing(false);
      await updateUserDetails();
      setMessage(
  isTelegramMiniApp
    ? t('tokenToUsdt.exchangeSuccessStars')
    : t('tokenToUsdt.exchangeSuccess')
);

    } catch (error) {
      console.error('Error requesting exchange:', error);
      setMessage(t('tokenToUsdt.exchangeProcessingError'));
      setIsProcessing(false);
    }
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    if (!clientX) return;

    const sliderTrack = document.querySelector(".tokenToUsdt-slider-track");
    const newAmount = calculateSelectedAmount(clientX, sliderTrack);
    setSelectedAmount(exchangeOptions[newAmount]);
  };

  const calculateSelectedAmount = (clientX, trackElement) => {
    const rect = trackElement.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const trackWidth = rect.width;
    const percent = (clickX / trackWidth) * 100;
    const adjustedPercent = (percent - 5) / (90 / (exchangeOptions.length - 1));
    return Math.round(Math.min(Math.max(adjustedPercent, 0), exchangeOptions.length - 1));
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const clientX = e.clientX || e.touches?.[0]?.clientX;
      if (!clientX) return;

      const sliderTrack = document.querySelector(".tokenToUsdt-slider-track");
      const newAmount = calculateSelectedAmount(clientX, sliderTrack);
      setSelectedAmount(exchangeOptions[newAmount]);
    };

    const handleEnd = () => setIsDragging(false);

    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd, { passive: false });
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);

    return () => {
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
    };
  }, [isDragging, exchangeOptions]);

  return (
    <div className="tokenToUsdt-page-container">
      <h2 className="tokenToUsdt-page-title">{t('tokenToUsdt.exchangeTokens')}</h2>

      {(exchangeOptions.length === 0) ? (
        <p className="tokenToUsdt-message">{t('tokenToUsdt.notEnoughTokens')}</p>
      ) : (
        <>
          <div className="tokenToUsdt-amount-display">
            <img src={commonIcons.tokenYellow} alt={t('tokens')} className="tokenToUsdt-currency-icon" />
            <span className="tokenToUsdt-selected-amount">{selectedAmount}</span>
            <span className="tokenToUsdt-divider">/ {tokenBalance}</span>
            <span className="tokenToUsdt-selected-amount"> =</span>
           
           
    {isTelegramMiniApp ? (
  <>
    <img src={commonIcons.starIcon} alt="Stars" className="tokenToUsdt-currency-icon" />
    <span className="tokenToUsdt-usdt-amount">{Math.floor(selectedAmount / 10)}</span>
  </>
) : (
  <>
    <img src={commonIcons.usdtIcon} alt="USDT" className="tokenToUsdt-currency-icon" />
    <span className="tokenToUsdt-usdt-amount">{(selectedAmount / 1000).toFixed(2)}</span>
  </>
)}

          </div>

          <form onSubmit={handleSubmit} className="tokenToUsdt-form">
            <div className={`tokenToUsdt-slider-container ${user.wallets.token < 100 ? 'disabled' : ''}`}>
              <div
                className="tokenToUsdt-slider-track"
                onMouseDown={() => user.wallets.token >= 100 && setIsDragging(true)}
                onTouchStart={() => user.wallets.token >= 100 && setIsDragging(true)}
                onPointerUp={user.wallets.token >= 100 ? handleTrackClick : undefined}
              >
                <div className="tokenToUsdt-slider-clickable-overlay" />
                <div className="tokenToUsdt-slider-dots">
                  {exchangeOptions.map((_, index) => (
                    <div key={index} className="tokenToUsdt-dot"></div>
                  ))}
                </div>
                <div
                  className="tokenToUsdt-slider-handle"
                  style={{
                    left: exchangeOptions.length === 1 
                      ? '95%' 
                      : `calc(5% + ${(exchangeOptions.indexOf(selectedAmount) * (90 / (exchangeOptions.length - 1)))}%)`,
                    opacity: user.wallets.token < 100 ? 0.5 : 1
                  }}
                ></div>
              </div>
            </div>

            <button
              type="submit"
              className="base-item-box tokenToUsdt-submit-button"
              disabled={isProcessing || selectedAmount === 0}
            >
              <span className="base-item-content tokenToUsdt-submit">
                {isProcessing ? (
                  <>
                    <span className="tokenToUsdt-spinner"></span> {t('tokenToUsdt.processing')}
                  </>
                ) : t('tokenToUsdt.exchange')}
              </span>
            </button>
            {message && <p className="tokenToUsdt-message">{message}</p>}
          </form>
        </>
      )}
    </div>
  );
};

const TokenToUsdtPage = (props) => {
  return <TokenToUsdtPageComponent {...props} />;
};

export default TokenToUsdtPage;